import { createAction, createReducer } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { apiCallBegan } from 'store/apiCalls'
import { gameIdSelector } from 'store/user/selectors'
import { PRODUCT_TYPES } from 'constants/productTypes'

// Map API keys to new product types
const API_KEY_TO_PRODUCT_TYPE = {
  'h-basic': 'primo',
  'h-plus': 'duo',
  'h-ultra': 'ultimo',
  'next-gen': 'nuovo',
  // Also handle snake_case variants
  h_basic: 'primo',
  h_plus: 'duo',
  h_ultra: 'ultimo',
  next_gen: 'nuovo'
}

// Default empty inventory for a plant
const DEFAULT_PLANT_INVENTORY = {
  [PRODUCT_TYPES[0]]: 0,
  [PRODUCT_TYPES[1]]: 0,
  [PRODUCT_TYPES[2]]: 0,
  [PRODUCT_TYPES[3]]: 0
}

// Transform API response keys to new product names
const transformApiKeys = (data) => {
  const transformed = {}
  Object.entries(data).forEach(([key, value]) => {
    const newKey = API_KEY_TO_PRODUCT_TYPE[key] || key
    transformed[newKey] = value
  })
  return transformed
}

// ACTIONS
const procurementRequested = createAction('procurement/requested')
const procurementRequestFailed = createAction('procurement/requestFailed')
const procurementReceived = createAction('procurement/received')

const procurementBuyRequested = createAction('procurement/buyRequested')
const procurementBuyRequestFailed = createAction('procurement/buyRequestFailed')
const procurementBuyReceived = createAction('procurement/buyReceived')

const procurementUpdateRequestedMaterial = createAction('procurement/updateRequestedMaterial')
const procurementClearPlant = createAction('procurement/clearPlant')

// REDUCER
export const initialState = {
  // Legacy: team-level buy (kept for backward compatibility)
  buy: { ...DEFAULT_PLANT_INVENTORY },
  // New: per-plant buy state
  buyByPlant: {},
  loading: false,
  buyLoading: false,
  success: false,
  buySuccess: false,
  loadingRequested: false,
  successRequested: false,
  currentPlantId: null
}

const procurementReducer = createReducer(initialState, {
  [procurementRequested.type]: (state, action) => {
    state.loadingRequested = true
    state.successRequested = false
    if (action.payload?.plantId) {
      state.currentPlantId = action.payload.plantId
    }
  },
  [procurementReceived.type]: (state, action) => {
    state.loadingRequested = false
    state.successRequested = true
    // API returns { status, message, code, data: { primo, duo, ... } }; use data or full payload
    const rawData = action.payload?.data ?? action.payload
    const transformedData =
      rawData && typeof rawData === 'object' && !Array.isArray(rawData)
        ? transformApiKeys(rawData)
        : { ...DEFAULT_PLANT_INVENTORY }
    // Use plantId from meta (per-plant request) or currentPlantId (fallback) or legacy buy
    const plantId = action.meta?.plantId || state.currentPlantId
    if (plantId) {
      state.buyByPlant[plantId] = transformedData
    } else {
      state.buy = transformedData
    }
  },
  [procurementRequestFailed.type]: (state, action) => {
    state.loadingRequested = false
    state.successRequested = false
    toast.error(action.payload.message)
  },
  // Buy raw materials
  [procurementBuyRequested.type]: (state, action) => {
    state.buyLoading = true
    state.buySuccess = false
    if (action.payload?.plantId) {
      state.currentPlantId = action.payload.plantId
    }
  },
  [procurementBuyReceived.type]: (state) => {
    state.buyLoading = false
    state.buySuccess = true
    // Keep buyByPlant values so the slider always shows what was ordered (until next load)
  },
  [procurementBuyRequestFailed.type]: (state, action) => {
    state.buyLoading = false
    state.buySuccess = false
    toast.error(action.payload.message)
  },
  [procurementUpdateRequestedMaterial.type]: (state, action) => {
    const { plantId, productType, quantity } = action.payload
    if (plantId) {
      // Per-plant update
      if (!state.buyByPlant[plantId]) {
        state.buyByPlant[plantId] = { ...DEFAULT_PLANT_INVENTORY }
      }
      state.buyByPlant[plantId][productType] = quantity
    } else {
      // Legacy team-level update
      state.buy[productType] = quantity
    }
  },
  [procurementClearPlant.type]: (state, action) => {
    const { plantId } = action.payload
    if (plantId && state.buyByPlant[plantId]) {
      state.buyByPlant[plantId] = { ...DEFAULT_PLANT_INVENTORY }
    }
  }
})

export default procurementReducer

// PUBLIC ACTIONS
export const updateRequestedMaterial =
  (productType, quantity, plantId = null) =>
  (dispatch) => {
    dispatch(procurementUpdateRequestedMaterial({ productType, quantity, plantId }))
  }

export const clearPlantProcurement = (plantId) => (dispatch) => {
  dispatch(procurementClearPlant({ plantId }))
}

// Legacy: load all requested raw materials (team-level)
export const loadRequestedRawMaterials = () => (dispatch, getState) => {
  const id = gameIdSelector(getState())

  dispatch(
    apiCallBegan({
      url: `/games/${id}/raw_materials/requested`,
      method: 'get',
      onStart: procurementRequested.type,
      onSuccess: procurementReceived.type,
      onError: procurementRequestFailed.type
    })
  )
}

// New: load requested raw materials for a specific plant
export const loadRequestedRawMaterialsForPlant = (plantId) => (dispatch, getState) => {
  const id = gameIdSelector(getState())

  dispatch(
    apiCallBegan({
      url: `/games/${id}/raw_materials/requested?plant_id=${plantId}`,
      method: 'get',
      onStart: { type: procurementRequested.type, payload: { plantId } },
      onSuccess: { type: procurementReceived.type, meta: { plantId } },
      onError: procurementRequestFailed.type
    })
  )
}

// Load requested raw materials for all active plants (used when entering Order or Receive action)
export const loadRequestedRawMaterialsForAllPlants = () => (dispatch, getState) => {
  const plants = getState().production?.data?.plants || []
  const activePlants = plants.filter((p) => p.status === 'bought' || p.status === 'rented')
  activePlants.forEach((plant) => dispatch(loadRequestedRawMaterialsForPlant(plant.id)))
}

// Legacy: buy raw materials (team-level)
export const buyRawMaterials = () => (dispatch, getState) => {
  const currentState = getState()
  const id = gameIdSelector(currentState)
  const { buy } = currentState.procurement

  const data = {
    h_basic_quantity: buy[PRODUCT_TYPES[0]],
    h_plus_quantity: buy[PRODUCT_TYPES[1]],
    h_ultra_quantity: buy[PRODUCT_TYPES[2]],
    next_gen_quantity: buy[PRODUCT_TYPES[3]]
  }

  dispatch(
    apiCallBegan({
      url: `/games/${id}/raw_materials/buy`,
      method: 'post',
      data,
      onStart: procurementBuyRequested.type,
      onSuccess: procurementBuyReceived.type,
      onError: procurementBuyRequestFailed.type
    })
  )
}

// New: buy raw materials for a specific plant
export const buyRawMaterialsForPlant = (plantId) => (dispatch, getState) => {
  const currentState = getState()
  const id = gameIdSelector(currentState)
  const plantBuy = currentState.procurement.buyByPlant[plantId] || DEFAULT_PLANT_INVENTORY

  const data = {
    plant_id: plantId,
    h_basic_quantity: plantBuy[PRODUCT_TYPES[0]],
    h_plus_quantity: plantBuy[PRODUCT_TYPES[1]],
    h_ultra_quantity: plantBuy[PRODUCT_TYPES[2]],
    next_gen_quantity: plantBuy[PRODUCT_TYPES[3]]
  }

  dispatch(
    apiCallBegan({
      url: `/games/${id}/raw_materials/buy`,
      method: 'post',
      data,
      onStart: { type: procurementBuyRequested.type, payload: { plantId } },
      onSuccess: { type: procurementBuyReceived.type, meta: { plantId } },
      onError: procurementBuyRequestFailed.type
    })
  )
}

// New: buy raw materials for all active plants (called by Primary Button)
export const buyRawMaterialsForAllPlants = () => async (dispatch, getState) => {
  const currentState = getState()
  const id = gameIdSelector(currentState)
  const { buyByPlant } = currentState.procurement
  const plants = currentState.production?.data?.plants || []
  const isTutorialYear =
    currentState.user?.team?.data?.current_year ===
    (currentState.user?.game?.data?.starting_year ?? 1)

  // Get active plants (bought or rented)
  const activePlants = plants.filter((p) => p.status === 'bought' || p.status === 'rented')

  // Get plants that have any materials to order
  const plantsWithOrders = activePlants.filter((plant) => {
    const quantities = buyByPlant[plant.id] || {}
    return Object.values(quantities).some((qty) => qty > 0)
  })

  // In tutorial year, must call API for Region A plant (let API validate the 2 Primo requirement)
  // In other years, if no orders, call API for first plant with zeros so backend transitions state
  if (plantsWithOrders.length === 0) {
    const plantToCall = isTutorialYear
      ? activePlants.find((p) => p.region_slug === 'a')
      : activePlants[0]
    if (plantToCall) {
      dispatch({ type: procurementBuyRequested.type })
      const quantities = buyByPlant[plantToCall.id] || {}
      dispatch(
        apiCallBegan({
          url: `/games/${id}/raw_materials/buy`,
          method: 'post',
          data: {
            plant_id: plantToCall.id,
            h_basic_quantity: quantities[PRODUCT_TYPES[0]] || 0,
            h_plus_quantity: quantities[PRODUCT_TYPES[1]] || 0,
            h_ultra_quantity: quantities[PRODUCT_TYPES[2]] || 0,
            next_gen_quantity: quantities[PRODUCT_TYPES[3]] || 0
          },
          onSuccess: { type: procurementBuyReceived.type, meta: { plantId: plantToCall.id } },
          onError: procurementBuyRequestFailed.type
        })
      )
      return
    }
    dispatch({ type: procurementBuyRequested.type })
    dispatch({ type: procurementBuyReceived.type })
    return
  }

  const orders = plantsWithOrders.map((plant) => {
    const quantities = buyByPlant[plant.id] || {}
    return {
      plant_id: plant.id,
      h_basic_quantity: quantities[PRODUCT_TYPES[0]] || 0,
      h_plus_quantity: quantities[PRODUCT_TYPES[1]] || 0,
      h_ultra_quantity: quantities[PRODUCT_TYPES[2]] || 0,
      next_gen_quantity: quantities[PRODUCT_TYPES[3]] || 0
    }
  })

  dispatch(
    apiCallBegan({
      url: `/games/${id}/raw_materials/buy_batch`,
      method: 'post',
      data: { orders },
      onStart: { type: procurementBuyRequested.type },
      onSuccess: procurementBuyReceived.type,
      onError: procurementBuyRequestFailed.type
    })
  )
}
